import React from "react";
import { Link } from "react-router-dom";
import { RiHome5Line } from "react-icons/ri";
import { useAdminContext } from "../../context/admin/context/AdminContext";
import {
  getOneLevel,
  getOneRegion,
  cleanOneLevel,
  getOneCountry,
  cleanOneRegion,
  cleanOneCountry,
} from "./BreadCrumb";

const dosee_home_route = "/";
const region_overview = "/region/manage";
const country_overview = "/countries";
const levels_overview = "/levels/first";

export const BreadCrumbComponent = ({ default_value = false }) => {
  const { sysAdmin, singleAdmin } = useAdminContext();

  // role-gate (match original behavior)
  const isMainAdmin = sysAdmin?.role === "super_admin";

  // get current selections
  let our_region = getOneRegion();
  let our_country = getOneCountry();
  let our_level = getOneLevel();

  // When default_value is true, show only the default crumb set
  if (default_value === true) {
    our_region = [];
    our_country = [];
    our_level = [];
  }

  // guards
  const isRegion = Array.isArray(our_region) && our_region.length > 0;
  const isCountry = Array.isArray(our_country) && our_country.length > 0;
  const isLevel = Array.isArray(our_level) && our_level.length > 0;

  // names (fallbacks preserved)
  const region_name = isRegion ? our_region[0]?.name : "Our Regions";
  const country_name = isCountry ? our_country[0]?.name : "Our Countries";
  const level_name = isLevel ? our_level[0]?.name : "Our Levels";

  // routes (fallbacks preserved)
  const region_route = isRegion ? our_region[0]?.route : region_overview;
  const country_route = isCountry ? our_country[0]?.route : country_overview;
  const level_route = isLevel ? our_level[0]?.route : levels_overview;

  // Normalize display titles (avoid empty strings)
  const norm = (v, fb) => (typeof v === "string" && v.trim().length > 0 ? v : fb);

  // Build crumb list (same order/logic as original)
  const Data = [
    {
      route: dosee_home_route,
      title: "Home",
      onClick: () => null,
      icon: <RiHome5Line className="h-4 w-4" />,
    },
    {
      route: region_route,
      title: norm(region_name, "Our Regions"),
      onClick: () => {
        cleanOneRegion();
        cleanOneCountry();
        cleanOneLevel();
      },
    },
    {
      route: country_route,
      title: norm(country_name, "Our Countries"),
      onClick: () => {
        cleanOneCountry();
        cleanOneLevel();
      },
    },
    {
      route: level_route,
      title: norm(level_name, "Our Levels"),
      onClick: () => cleanOneLevel(),
    },
  ];

  // Hide entirely if singleAdmin is set (matches your original)
  if (singleAdmin) return null;
  if (!isMainAdmin) return null;

  return (
    <nav
      aria-label="Breadcrumb"
      className="w-full overflow-x-auto overflow-y-hidden"
    >
      <ol className="my-2 inline-flex max-w-full items-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs shadow-sm">
        {Data.map((item, idx) => {
          const isLast = idx === Data.length - 1;
          return (
            <li key={idx} className="flex items-center">
              <BreadCrumb
                route={item.route}
                title={item.title}
                onClick={item.onClick}
                icon={item.icon}
                isLast={isLast}
              />
              {!isLast && (
                <span
                  aria-hidden="true"
                  className="mx-1 select-none text-gray-400"
                >
                  /
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

const BreadCrumb = ({ route, title, onClick, icon, isLast }) => {
  // Keep link behavior even on the last item to preserve original navigation logic.
  return (
    <Link
      to={route}
      onClick={onClick}
      aria-current={isLast ? "page" : undefined}
      className={[
        "group inline-flex max-w-[220px] items-center gap-1 rounded px-1 py-0.5",
        isLast
          ? "cursor-pointer text-gray-900"
          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
      ].join(" ")}
      title={title}
    >
      {icon ? (
        <span
          className={[
            "shrink-0",
            isLast ? "text-gray-900" : "text-gray-500 group-hover:text-gray-700",
          ].join(" ")}
        >
          {icon}
        </span>
      ) : null}

      <span
        className={[
          "truncate",
          isLast ? "font-semibold" : "hover:underline decoration-emerald-500",
        ].join(" ")}
      >
        {title}
      </span>
    </Link>
  );
};
